import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Skeleton,
  Stack,
  SvgIcon,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import { useNavigate } from 'react-router-dom';
import { DjPageShell } from '@/shared/ui/DjPageShell';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';
import { eventApi } from '@/entities/event/api/event.api';
import type { DjEvent, EventCounts, EventStatus } from '@/entities/event/model/event.types';
import { routePaths } from '@/app/router/routePaths';
import { formatEventTime, formatRecurrenceDays } from '@/shared/lib/eventTime';

const iconPaths = {
  plus: 'M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z',
  arrow: 'M12 4l-1.41 1.41L15.17 10H4v2h11.17l-4.58 4.59L12 18l7-7-7-7z',
  location: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
  calendar: 'M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z',
  music: 'M12 3v11.55A4 4 0 1 0 14 18V7h6V3h-8zM8 20a2 2 0 1 1 0-4 2 2 0 0 1 0 4z',
  archive: 'M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z',
  qr: 'M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm10-2h2v2h-2v-2zm4 0h2v4h-4v-2h2v-2zm-6 4h2v4h-2v-4zm4 2h4v2h-4v-2z',
  spotify: 'M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm4.586 14.424a.623.623 0 0 1-.857.207c-2.348-1.435-5.304-1.76-8.785-.964a.623.623 0 1 1-.277-1.215c3.809-.87 7.077-.496 9.712 1.115a.623.623 0 0 1 .207.857zm1.223-2.722a.78.78 0 0 1-1.072.257c-2.687-1.652-6.785-2.131-9.965-1.166a.78.78 0 0 1-.973-.519.78.78 0 0 1 .518-.973c3.632-1.102 8.147-.568 11.234 1.329a.78.78 0 0 1 .258 1.072zm.105-2.835C14.692 8.95 9.375 8.775 6.297 9.71a.937.937 0 1 1-.543-1.795c3.532-1.07 9.404-.862 13.115 1.338a.936.936 0 0 1 .045 1.614z',
  wave: 'M4 10h2v8H4v-8zm4-4h2v12H8V6zm4-3h2v15h-2V3zm4 6h2v9h-2V9zm4-4h2v13h-2V5z',
  share: 'M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z',
};

const SPOTIFY_GREEN = '#1DB954';

type FilterKey = 'ALL' | EventStatus;

const filters: { key: FilterKey; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'ACTIVE', label: 'Active' },
  { key: 'PENDING', label: 'Pending' },
  { key: 'CLOSED', label: 'Closed' },
  { key: 'ARCHIVED', label: 'Archived' },
];

const statusMeta: Record<EventStatus, { label: string; color: string; bg: string; border: string }> = {
  ACTIVE:   { label: 'Active',    color: '#10B981',               bg: 'rgba(16,185,129,0.13)',  border: 'rgba(16,185,129,0.3)'  },
  PENDING:  { label: 'Pending',   color: '#818CF8',               bg: 'rgba(129,140,248,0.13)', border: 'rgba(129,140,248,0.3)' },
  CLOSED:   { label: 'Closed',    color: '#F59E0B',               bg: 'rgba(245,158,11,0.13)',  border: 'rgba(245,158,11,0.3)'  },
  ARCHIVED: { label: 'Archived',  color: 'rgba(255,255,255,0.3)', bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.1)' },
};

const requestStatusMeta = {
  OPEN:   { label: 'Requests open',   dot: '#10B981' },
  PAUSED: { label: 'Requests paused', dot: '#F59E0B' },
  CLOSED: { label: 'Requests closed', dot: '#EF4444' },
};

function AppIcon({ path, ...props }: SvgIconProps & { path: string }) {
  return <SvgIcon {...props}><path d={path} /></SvgIcon>;
}

function formatEventDate(dateStr?: string): string {
  if (!dateStr) return 'No date set';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

function EventCard({ event, onDelete }: { event: DjEvent; onDelete: (event: DjEvent) => void }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const sm = statusMeta[event.status];
  const rm = requestStatusMeta[event.requestStatus];

  const handleCardClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('[data-action]')) return;
    navigate(routePaths.djEventDetail.replace(':eventId', event.eventId));
  };

  return (
    <Box
      onClick={handleCardClick}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        p: { xs: 2, sm: 2.5 },
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
        bgcolor: alpha(theme.palette.common.white, 0.065),
        backdropFilter: 'blur(20px)',
        boxShadow: `0 18px 44px ${alpha(theme.palette.common.black, 0.16)}`,
        cursor: 'pointer',
        transition: 'transform 0.18s, background-color 0.18s, border-color 0.18s, box-shadow 0.18s',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          background: event.status === 'ACTIVE'
            ? `linear-gradient(180deg, ${theme.palette.success.light}, ${theme.palette.secondary.light})`
            : event.status === 'PENDING'
              ? `linear-gradient(180deg, ${theme.palette.warning.light}, ${theme.palette.primary.light})`
              : alpha(theme.palette.common.white, 0.16),
        },
        '&:hover': {
          bgcolor: alpha(theme.palette.common.white, 0.095),
          borderColor: alpha(theme.palette.secondary.light, 0.28),
          transform: 'translateY(-2px)',
          boxShadow: `0 16px 40px ${alpha(theme.palette.common.black, 0.32)}`,
        },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip
            label={sm.label}
            size="small"
            sx={{ height: 22, fontSize: 11, fontWeight: 800, color: sm.color, bgcolor: sm.bg, border: `1px solid ${sm.border}`, '& .MuiChip-label': { px: 0.875 } }}
          />
          <Stack direction="row" alignItems="center" spacing={0.6}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: rm.dot, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 11.5, color: alpha(theme.palette.common.white, 0.4), fontWeight: 600 }}>
              {rm.label}
            </Typography>
          </Stack>
        </Stack>

        {event.status !== 'ARCHIVED' && (
          <Box
            data-action="delete"
            onClick={(e) => { e.stopPropagation(); onDelete(event); }}
            sx={{
              p: 0.75,
              borderRadius: 1,
              color: alpha(theme.palette.common.white, 0.25),
              cursor: 'pointer',
              transition: 'color 0.15s, background 0.15s',
              '&:hover': { color: theme.palette.error.light, bgcolor: alpha(theme.palette.error.main, 0.12) },
            }}
          >
            <AppIcon path={iconPaths.archive} sx={{ fontSize: 18, display: 'block' }} />
          </Box>
        )}
      </Stack>

      <Typography sx={{ fontSize: { xs: 18, sm: 20 }, fontWeight: 900, color: 'common.white', lineHeight: 1.2, mb: 1.25 }}>
        {event.name}
      </Typography>

      <Stack spacing={0.6} sx={{ mb: 1.75 }}>
        {event.venueName && (
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <AppIcon path={iconPaths.location} sx={{ fontSize: 15, color: alpha(theme.palette.common.white, 0.35), flexShrink: 0 }} />
            <Typography noWrap sx={{ fontSize: 13, color: alpha(theme.palette.common.white, 0.52), fontWeight: 500 }}>
              {event.venueName}
            </Typography>
          </Stack>
        )}
        {event.recurrence === 'weekly' && event.recurrenceDays?.length ? (
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <AppIcon path={iconPaths.calendar} sx={{ fontSize: 15, color: alpha(theme.palette.common.white, 0.35), flexShrink: 0 }} />
            <Typography sx={{ fontSize: 13, color: alpha(theme.palette.common.white, 0.52), fontWeight: 500 }}>
              Every {formatRecurrenceDays(event.recurrenceDays)}
              {event.startTime && (
                <Box component="span" sx={{ ml: 1, color: alpha(theme.palette.common.white, 0.35) }}>
                  {formatEventTime(new Date().toISOString().slice(0, 10), event.startTime, event.endTime)}
                </Box>
              )}
              {event.recurrenceEndDate && (
                <Box component="span" sx={{ ml: 1, color: alpha(theme.palette.common.white, 0.28), fontSize: 11 }}>
                  · until {new Date(event.recurrenceEndDate + 'T00:00:00').toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}
                </Box>
              )}
            </Typography>
          </Stack>
        ) : (event.eventDate || event.startTime) && (
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <AppIcon path={iconPaths.calendar} sx={{ fontSize: 15, color: alpha(theme.palette.common.white, 0.35), flexShrink: 0 }} />
            <Typography sx={{ fontSize: 13, color: alpha(theme.palette.common.white, 0.52), fontWeight: 500 }}>
              {formatEventDate(event.eventDate)}
              {event.startTime && (
                <Box component="span" sx={{ ml: 1, color: alpha(theme.palette.common.white, 0.35) }}>
                  {formatEventTime(event.eventDate, event.startTime, event.endTime)}
                </Box>
              )}
            </Typography>
          </Stack>
        )}
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={0.75} flexWrap="wrap">
          {event.spotifyPlaylistName && (
            <Chip
              icon={<AppIcon path={iconPaths.spotify} sx={{ fontSize: '13px !important' }} />}
              label={event.spotifyPlaylistName}
              size="small"
              sx={{
                height: 22, maxWidth: 160, fontSize: 11, fontWeight: 700,
                color: SPOTIFY_GREEN, bgcolor: alpha(SPOTIFY_GREEN, 0.1), border: `1px solid ${alpha(SPOTIFY_GREEN, 0.25)}`,
                '& .MuiChip-icon': { color: SPOTIFY_GREEN },
                '& .MuiChip-label': { px: 0.75, overflow: 'hidden', textOverflow: 'ellipsis' },
              }}
            />
          )}
          {event.spotifyPlaylistId && event.sharePlaylist && (
            <Chip
              icon={<AppIcon path={iconPaths.share} sx={{ fontSize: '13px !important' }} />}
              label="Playlist shared"
              size="small"
              sx={{
                height: 22, fontSize: 11, fontWeight: 700,
                color: alpha(theme.palette.common.white, 0.65), bgcolor: alpha(theme.palette.common.white, 0.07),
                border: `1px solid ${alpha(theme.palette.common.white, 0.14)}`,
                '& .MuiChip-icon': { color: alpha(theme.palette.common.white, 0.4) },
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          )}
          {event.qrCodeUrl && (
            <Chip
              icon={<AppIcon path={iconPaths.qr} sx={{ fontSize: '13px !important' }} />}
              label="QR ready"
              size="small"
              sx={{
                height: 22, fontSize: 11, fontWeight: 700,
                color: alpha(theme.palette.primary.light, 0.9), bgcolor: alpha(theme.palette.primary.main, 0.12),
                border: `1px solid ${alpha(theme.palette.primary.light, 0.25)}`,
                '& .MuiChip-icon': { color: theme.palette.primary.light },
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          )}
        </Stack>
        <AppIcon path={iconPaths.arrow} sx={{ fontSize: 20, color: alpha(theme.palette.common.white, 0.22), flexShrink: 0 }} />
      </Stack>
    </Box>
  );
}

function EventSkeleton() {
  const theme = useTheme();
  return (
    <Box sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 1.5, border: `1px solid ${alpha(theme.palette.common.white, 0.07)}`, bgcolor: alpha(theme.palette.common.white, 0.04) }}>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Skeleton variant="rounded" width={64} height={22} sx={{ bgcolor: alpha('#fff', 0.07), borderRadius: 99 }} />
        <Skeleton variant="circular" width={24} height={24} sx={{ bgcolor: alpha('#fff', 0.05) }} />
      </Stack>
      <Skeleton variant="text" width="65%" height={26} sx={{ bgcolor: alpha('#fff', 0.08), mb: 1 }} />
      <Skeleton variant="text" width="45%" height={16} sx={{ bgcolor: alpha('#fff', 0.06) }} />
      <Skeleton variant="text" width="55%" height={16} sx={{ bgcolor: alpha('#fff', 0.05), mt: 0.5 }} />
    </Box>
  );
}

function EmptyState({ filter, onCreateClick }: { filter: FilterKey; onCreateClick: () => void }) {
  const theme = useTheme();
  const isLazy = filter === 'CLOSED' || filter === 'ARCHIVED';
  return (
    <Box sx={{ py: { xs: 5, sm: 8 }, px: 3, borderRadius: 2.25, border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`, bgcolor: alpha(theme.palette.common.white, 0.05), backdropFilter: 'blur(18px)', textAlign: 'center', boxShadow: `0 20px 48px ${alpha(theme.palette.common.black, 0.16)}` }}>
      <Box sx={{ width: 60, height: 60, borderRadius: '34% 46% 36% 44%', bgcolor: alpha(theme.palette.common.white, 0.06), border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`, display: 'grid', placeItems: 'center', mx: 'auto', mb: 2, color: alpha(theme.palette.common.white, 0.28), transform: 'rotate(-4deg)' }}>
        <AppIcon path={iconPaths.wave} sx={{ fontSize: 28 }} />
      </Box>
      <Typography sx={{ fontSize: 18, fontWeight: 800, color: alpha(theme.palette.common.white, 0.65) }}>
        {filter === 'ALL' ? 'No live events' : `No ${statusMeta[filter as EventStatus]?.label.toLowerCase() ?? filter.toLowerCase()} events`}
      </Typography>
      <Typography sx={{ mt: 0.75, fontSize: 14, color: alpha(theme.palette.common.white, 0.38) }}>
        {filter === 'ALL'
          ? 'Create your first event and let the crowd in.'
          : isLazy
          ? `No events with this status yet.`
          : 'Try a different filter or create a new event.'}
      </Typography>
      {filter === 'ALL' && (
        <Button
          onClick={onCreateClick}
          variant="outlined"
          size="small"
          sx={{ mt: 2.5, color: alpha(theme.palette.common.white, 0.7), borderColor: alpha(theme.palette.common.white, 0.2), '&:hover': { borderColor: 'secondary.light', bgcolor: alpha(theme.palette.secondary.main, 0.14), color: 'common.white' } }}
        >
          Create event
        </Button>
      )}
    </Box>
  );
}

export function DjEventsPage() {
  const theme = useTheme();
  const navigate = useNavigate();

  const [liveEvents, setLiveEvents] = useState<DjEvent[]>([]);
  const [closedEvents, setClosedEvents] = useState<DjEvent[] | null>(null);
  const [archivedEvents, setArchivedEvents] = useState<DjEvent[] | null>(null);
  const [counts, setCounts] = useState<EventCounts | null>(null);

  const [loadingLive, setLoadingLive] = useState(true);
  const [loadingClosed, setLoadingClosed] = useState(false);
  const [loadingArchived, setLoadingArchived] = useState(false);

  const [filter, setFilter] = useState<FilterKey>('ALL');
  const [deleteTarget, setDeleteTarget] = useState<DjEvent | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    Promise.all([
      eventApi.listDjEvents(),
      eventApi.getDjEventCounts(),
    ]).then(([events, eventCounts]) => {
      setLiveEvents([...events].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
      setCounts(eventCounts);
    }).finally(() => setLoadingLive(false));
  }, []);

  useEffect(() => {
    if (filter === 'CLOSED' && closedEvents === null && !loadingClosed) {
      setLoadingClosed(true);
      eventApi.listDjEvents('CLOSED')
        .then((data) => setClosedEvents([...data].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())))
        .finally(() => setLoadingClosed(false));
    }
    if (filter === 'ARCHIVED' && archivedEvents === null && !loadingArchived) {
      setLoadingArchived(true);
      eventApi.listDjEvents('ARCHIVED')
        .then((data) => setArchivedEvents([...data].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())))
        .finally(() => setLoadingArchived(false));
    }
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleArchive = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await eventApi.deleteEvent(deleteTarget.eventId);
      const archivedVersion: DjEvent = { ...deleteTarget, status: 'ARCHIVED' };
      setLiveEvents((prev) => prev.filter((e) => e.eventId !== deleteTarget.eventId));
      setCounts((prev) => prev ? {
        ...prev,
        [deleteTarget.status]: Math.max(0, (prev[deleteTarget.status as keyof EventCounts] ?? 0) - 1),
        ARCHIVED: prev.ARCHIVED + 1,
      } : prev);
      setArchivedEvents((prev) => prev ? [archivedVersion, ...prev] : null);
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const getVisible = (): DjEvent[] => {
    switch (filter) {
      case 'ALL':     return liveEvents;
      case 'ACTIVE':  return liveEvents.filter((e) => e.status === 'ACTIVE');
      case 'PENDING': return liveEvents.filter((e) => e.status === 'PENDING');
      case 'CLOSED':  return closedEvents ?? [];
      case 'ARCHIVED': return archivedEvents ?? [];
    }
  };

  const getCount = (key: FilterKey): number => {
    if (!counts) return 0;
    if (key === 'ALL') return counts.ACTIVE + counts.PENDING;
    return counts[key as EventStatus] ?? 0;
  };

  const isLoading = () => {
    if (filter === 'CLOSED') return loadingClosed;
    if (filter === 'ARCHIVED') return loadingArchived;
    return loadingLive;
  };

  const visible = getVisible();
  const loading = isLoading();
  const countsReady = counts !== null;

  return (
    <DjPageShell>
      <Stack spacing={{ xs: 3, sm: 4 }}>

        {/* Header */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'flex-end' }}
          justifyContent="space-between"
          spacing={2}
          sx={{
            position: 'relative',
            overflow: 'hidden',
            p: { xs: 2, sm: 2.5 },
            borderRadius: 2.25,
            border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
            bgcolor: alpha(theme.palette.common.white, 0.055),
            backdropFilter: 'blur(20px)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -80,
              right: -130,
              width: 300,
              height: 200,
              pointerEvents: 'none',
              background: `linear-gradient(115deg, transparent 0 24%, ${alpha(theme.palette.secondary.light, 0.2)} 24% 36%, transparent 36% 52%, ${alpha(theme.palette.warning.light, 0.14)} 52% 62%, transparent 62%)`,
              transform: 'skewY(-8deg)',
            },
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ position: 'relative', fontSize: { xs: 30, sm: 38 }, fontWeight: 900, color: 'common.white', lineHeight: 1.05 }}>
              My Events
            </Typography>
            <Typography sx={{ position: 'relative', mt: 0.5, fontSize: 15, color: alpha(theme.palette.common.white, 0.58), fontWeight: 500 }}>
              {!countsReady ? 'Loading your sets…' : `${counts.ACTIVE + counts.PENDING} live · ${counts.CLOSED} closed · ${counts.ARCHIVED} archived`}
            </Typography>
          </Box>
          <Button
            onClick={() => navigate(routePaths.djCreateEvent)}
            variant="contained"
            startIcon={<AppIcon path={iconPaths.plus} />}
            sx={{
              position: 'relative', flexShrink: 0, minHeight: 46, px: 2.5, borderRadius: 1.5,
              bgcolor: 'secondary.main',
              boxShadow: `0 10px 28px ${alpha(theme.palette.secondary.main, 0.36)}`,
              '&:hover': { bgcolor: 'secondary.dark', boxShadow: `0 14px 32px ${alpha(theme.palette.secondary.main, 0.44)}` },
            }}
          >
            New Event
          </Button>
        </Stack>

        {/* Filter bar */}
        <Box sx={{ display: 'flex', gap: 0.5, overflowX: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' }, pb: 0.25 }}>
          <Box sx={{ display: 'inline-flex', gap: 0.5, p: 0.5, borderRadius: 99, border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`, bgcolor: alpha(theme.palette.common.white, 0.06), backdropFilter: 'blur(16px)', flexShrink: 0 }}>
            {filters.map((f) => {
              const active = filter === f.key;
              const count = getCount(f.key);
              return (
                <Button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  size="small"
                  sx={{
                    px: 1.5, py: 0.75, minWidth: 0, whiteSpace: 'nowrap',
                    color: active ? 'common.white' : alpha(theme.palette.common.white, 0.55),
                    bgcolor: active ? alpha(theme.palette.common.white, 0.12) : 'transparent',
                    borderRadius: 99, fontWeight: active ? 900 : 600, fontSize: 13.5, transition: 'all 0.15s',
                    ...(active && { boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.common.white, 0.15)}` }),
                    '&:hover': { color: 'common.white', bgcolor: alpha(theme.palette.common.white, 0.1) },
                  }}
                >
                  {f.label}
                  {countsReady && count > 0 && (
                    <Box component="span" sx={{ ml: 0.75, px: 0.625, minWidth: 18, height: 18, borderRadius: 99, fontSize: 11, fontWeight: 800, lineHeight: '18px', textAlign: 'center', display: 'inline-block', color: active ? '#fff' : alpha(theme.palette.common.white, 0.4), bgcolor: active ? alpha(theme.palette.secondary.main, 0.55) : alpha(theme.palette.common.white, 0.08) }}>
                      {count}
                    </Box>
                  )}
                </Button>
              );
            })}
          </Box>
        </Box>

        {/* Content */}
        {loading ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 1.5 }}>
            {Array.from({ length: 6 }).map((_, i) => <EventSkeleton key={i} />)}
          </Box>
        ) : visible.length === 0 ? (
          <EmptyState filter={filter} onCreateClick={() => navigate(routePaths.djCreateEvent)} />
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 1.5 }}>
            {visible.map((event) => (
              <EventCard key={event.eventId} event={event} onDelete={setDeleteTarget} />
            ))}
          </Box>
        )}
      </Stack>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={`Archive "${deleteTarget?.name}"?`}
        message="This will archive the event and close all pending requests. It will no longer appear in your active events."
        confirmText={deleting ? 'Archiving…' : 'Archive event'}
        cancelText="Keep it"
        onConfirm={handleArchive}
        onCancel={() => setDeleteTarget(null)}
      />
    </DjPageShell>
  );
}
